import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { api, errorMessage } from '../api';
import { Alert, Button, ErrorAlert, Field, Input, Loading } from '../components/ui';
import { useAuth } from './AuthContext';
import { ChangePasswordForm } from './ChangePassword';

function AuthCard({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="auth-screen">
      <div className="card auth-card">
        <div className="card-body">
          <div className="auth-brand">
            <img src="/euro-marble-logo.png" alt="Euro Marble" width={200} height={80} />
            <span>Time and attendance</span>
          </div>
          <h1>{title}</h1>
          {subtitle && <p className="muted">{subtitle}</p>}
          <div style={{ marginTop: 16 }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Sign-in flow: password, then authenticator code; first sign-in sets up the
 * authenticator; a temporary password must be replaced before continuing.
 */
export function LoginPage() {
  const { session, notice, acceptToken, signOut } = useAuth();
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  if (session?.mfaSetupRequired) return <TotpSetup onCancel={signOut} />;
  if (session?.mustChangePassword) return <ForcedPasswordChange />;
  if (mfaToken) return <MfaStep mfaToken={mfaToken} onBack={() => setMfaToken(null)} onToken={acceptToken} />;
  return <PasswordStep notice={notice} onMfa={setMfaToken} onToken={acceptToken} />;
}

function PasswordStep({ notice, onMfa, onToken }: { notice: string | null; onMfa: (t: string) => void; onToken: (t: string) => Promise<unknown> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.login(email.trim(), password);
      if (r.mfaRequired && r.mfaToken) onMfa(r.mfaToken);
      else if (r.token) await onToken(r.token);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Sign in" subtitle="Management portal for supervisors, payroll and owners.">
      <form className="stack" onSubmit={submit}>
        {notice && <Alert tone="accent">{notice}</Alert>}
        <ErrorAlert error={error} />
        <Field label="Email">
          <Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        </Field>
        <Field label="Password">
          <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        <Button type="submit" variant="primary" size="lg" block loading={busy}>
          Continue
        </Button>
      </form>
    </AuthCard>
  );
}

function MfaStep({ mfaToken, onBack, onToken }: { mfaToken: string; onBack: () => void; onToken: (t: string) => Promise<unknown> }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.mfa(mfaToken, code.replace(/\s/g, ''));
      await onToken(r.token);
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Authenticator code" subtitle="Enter the 6-digit code from your authenticator app.">
      <form className="stack" onSubmit={submit}>
        <Field label="Code" error={error}>
          <Input
            className="code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={7}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            autoFocus
          />
        </Field>
        <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={code.replace(/\s/g, '').length < 6}>
          Sign in
        </Button>
        <Button variant="ghost" onClick={onBack}>
          Use a different account
        </Button>
      </form>
    </AuthCard>
  );
}

function TotpSetup({ onCancel }: { onCancel: () => void }) {
  const { refresh } = useAuth();
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Each setup call issues a new secret, so make exactly one even when StrictMode re-runs the effect.
  const setupCall = useRef<Promise<{ secret: string; qr: string }> | null>(null);
  useEffect(() => {
    let live = true;
    setupCall.current ??= api.totpSetup().then(async (r) => ({ secret: r.secret, qr: await QRCode.toDataURL(r.otpauthUrl, { margin: 1, width: 200 }) }));
    setupCall.current.then((s) => live && setSetup(s)).catch((e) => live && setLoadError(e));
    return () => {
      live = false;
    };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.totpEnable(code.replace(/\s/g, ''));
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Set up your authenticator" subtitle="The portal needs a second factor. Use Google Authenticator, Microsoft Authenticator or similar.">
      {loadError ? (
        <div className="stack">
          <ErrorAlert error={loadError} />
          <Button onClick={onCancel}>Back to sign in</Button>
        </div>
      ) : !setup ? (
        <Loading label="Preparing" />
      ) : (
        <form className="stack" onSubmit={submit}>
          <ol style={{ margin: 0, paddingLeft: 18 }} className="stack tight">
            <li>Scan this QR code with your authenticator app.</li>
            <li>If you cannot scan it, add the key below by hand (time-based).</li>
            <li>Enter the 6-digit code the app shows.</li>
          </ol>
          <div className="qr">
            <img src={setup.qr} width={200} height={200} alt="QR code for your authenticator app" />
          </div>
          <div>
            <div className="small muted">Setup key</div>
            <div className="secret">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</div>
          </div>
          <Field label="Code from the app" error={error}>
            <Input className="code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} required />
          </Field>
          <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={code.replace(/\s/g, '').length < 6}>
            Turn on and continue
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel and sign out
          </Button>
        </form>
      )}
    </AuthCard>
  );
}

function ForcedPasswordChange() {
  const { refresh, signOut } = useAuth();
  return (
    <AuthCard title="Choose a new password" subtitle="You signed in with a temporary password. Choose your own before continuing.">
      <ChangePasswordForm onDone={() => void refresh()} onCancel={() => void signOut()} cancelLabel="Sign out" />
    </AuthCard>
  );
}
